import { useState, useCallback, useRef, useEffect } from "react";

import Box from "@mui/material/Box"
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from "@mui/material/FormControl";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import GavelIcon from '@mui/icons-material/Gavel';
import TextField from "@mui/material/TextField";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { HttpStatusCode } from "axios";
import { isEmpty, isEqual, isNil } from "lodash"; 
import { isNull } from "lodash/lang";
import authService from "@plannel/services/system/auth-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import targetInvSimulationService from "@plannel/services/ip/target-inv-simulation-service";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import dateUtils from '@plannel/utils/date-util';
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";

import {capitalCase, snakeCase} from "change-case";
import { IpTargetLocationItemHrchyComponentContent, IpTargetLocationItemHrchyEnabledComponent } from "../ip-config/IpSettings";
import planConfigService from "@plannel/services/data/plan-config-service";
import { useDispatch } from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";

const {
  IP_SETTINGS_CONFIG_CD,
  TARGET_LOCATION, 
  SIM_TARGET_FILTER_ENABLED,
  SIM_TARGET_FILTER_POLICY,
  LOCATION_NODE_TREE,
  TARGET_ITEM_HRCHY,
  ITEM_HRCHY_NODE_TREE
} = CodeUtil.ipSettings;
const { RELEASED, UNPLANNED } = CodeUtil.versionStatusCode;
/* IP 버전이 사용한 ABC-XYZ 분류 id 는 planConfigObj.CLASSIFICATION 에 저장된다 (IpPlanVersionDto). */
const CLASSIFICATION = "CLASSIFICATION";

// ── 모달 폭 ──
// 이 앱엔 전역 CssBaseline(box-sizing 리셋)이 없어서 Modal Box 의 width 는 패딩을 뺀 콘텐츠 폭이다.
// 고급 설정을 켜면 우측 패널이 붙는데, 폭이 콘텐츠보다 좁으면 모달에 가로 스크롤이 생기므로 실제 폭을 그대로 더한다.
const MODAL_WIDTH = 350;                                                            // 좌측 입력 영역
const ADVANCED_PANEL_WIDTH = 316;                                                   // 우측 패널 = Location/Item Hierarchy 300 + 각 컴포넌트의 mr 2
const ADVANCED_PANEL_GAP = 16;                                                      // 우측 패널 Box 의 ml 2
const MODAL_WIDTH_EXPANDED = MODAL_WIDTH + ADVANCED_PANEL_GAP + ADVANCED_PANEL_WIDTH;
const CreateScenarioModal = ({ t, viewName, modalOpen, reload, setModalOpen, setSnackInfo, planVersions, retrievePlanVersion, iconColor, classificationList }) => {
  const reduxDispatch = useDispatch();

  const INV_PLC_MS = {
    PLC_TP: capitalCase("POLICY_TYPE"),
    SVC_LVL: capitalCase("TARGET_QTY"),
    INV_DAYS: capitalCase("INVENTORY_DAYS")
  };

  const { snackBarMessageColor } = ColorUtil;
  const moduleCd = CodeUtil.module.IP;
  const defaultPolicy = {
    [`a_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: null,
  }

  const scenarioBase = {
    id: null,
    name: "",
    owner: authService.getCurrentUser().username,
    createdBy: null,
    statusCd: UNPLANNED,
    descTxt: "",
    policyObj: defaultPolicy,
    verNum: 0,
  }

  const setDefaultScenario = () => {
      const defaultPolicyType = "norDist";
  
      const policyHigh = {
        [`ax_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`ay_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`az_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`bx_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`by_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`bz_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`cx_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`cy_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`cz_${snakeCase(INV_PLC_MS.PLC_TP)}`]: defaultPolicyType,
        [`ax_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 97,
        [`ay_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 95,
        [`az_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 93,
        [`bx_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 91,
        [`by_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 90,
        [`bz_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 89,
        [`cx_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 85,
        [`cy_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 80,
        [`cz_${snakeCase(INV_PLC_MS.SVC_LVL)}`]: 70,
        "AX": [defaultPolicyType, 97, true, true, true, false],
        "AY": [defaultPolicyType, 95, true, true, true, false],
        "AZ": [defaultPolicyType, 93, true, true, true, false],
        "BX": [defaultPolicyType, 91, true, true, true, false],
        "BY": [defaultPolicyType, 90, true, true, true, false],
        "BZ": [defaultPolicyType, 89, true, true, true, false],
        "CX": [defaultPolicyType, 85, true, true, true, false],
        "CY": [defaultPolicyType, 80, true, true, true, false],
        "CZ": [defaultPolicyType, 70, false, true, true, false]
      }
  
      const defaultServiceLevel = {
        ...scenarioBase,
        policyObj: policyHigh,
        newRow: true,
        name: "",
        descTxt: ""
      };

      return defaultServiceLevel;
  }

  const defaultScenario = setDefaultScenario();
  const [newScenarioRow, setNewScenarioRow] = useState(defaultScenario);
  const [classification, setClassification] = useState(null);
  const selectedData = useRef(defaultScenario);

  const masterData = { id: null, name: t("masterData"), descTxt: "" };
  const [sourceVersion, setSourceVersion] = useState(masterData);
  const [sourceData, setSourceData] = useState([]);

  const [filterValue, setFilterValue] = useState({locations: [], category: []});
  const [simTargetFilterPolicy, setSimTargetFilterPolicy] = useState({ [SIM_TARGET_FILTER_ENABLED]: false, [TARGET_LOCATION]: [], [LOCATION_NODE_TREE]: [], [TARGET_ITEM_HRCHY]: [], [ITEM_HRCHY_NODE_TREE]: [] });

  /* Source Data 버전이 사용했던 분류를 찾는다. id 는 BigInt 로 넘어올 수 있어 문자열로 비교한다.
     목록(PLANNED/READY)에 없는 분류라면 null — 사용자가 직접 고르게 둔다. */
  const findClassificationOfVersion = (version) => {
    const classificationId = version?.planConfigObj?.[CLASSIFICATION];
    if (isNil(classificationId)) {
      return null;
    }

    return classificationList.find(item => isEqual(String(item.id), String(classificationId))) ?? null;
  }

  const onChangeSourceData = (newValue) => {
    setSourceVersion(newValue);

    /* 고른 버전이 실제로 쓴 ABC-XYZ 분류를 그대로 따라간다 (Master Data 는 분류가 없어 초기화). */
    setClassification(findClassificationOfVersion(newValue));
  }

  const setPolicy = (objVal) => {
    if (!isEmpty(objVal)) {
      const {
        SIM_TARGET_FILTER_POLICY,
      } = objVal;
      setSimTargetFilterPolicy(SIM_TARGET_FILTER_POLICY);

      /* Filter Value Setting [LOCATION_NODE_TREE, ITEM_HRCHY_NODE_TREE] 
      * filterValue: { locations: [], category: [] } Node Tree Data 형식이므로 셋팅.
      */

      const locationTargetTreeNode = SIM_TARGET_FILTER_POLICY?.[LOCATION_NODE_TREE] || [];
      const itemHrchyTargetTreeNode = SIM_TARGET_FILTER_POLICY?.[ITEM_HRCHY_NODE_TREE] || [];
      setFilterValue({ locations: locationTargetTreeNode, category: itemHrchyTargetTreeNode });

      const viewState = reduxUtil.createObjectState(viewName, { filter: { category: itemHrchyTargetTreeNode, locations: locationTargetTreeNode } });
      reduxDispatch(reduxUtil.updateViewState(viewState));
    }
  }

  const IP_CONFIG_PARAMS = { moduleCd: "IP", configCd: IP_SETTINGS_CONFIG_CD };
  const retrieveIpConfig = () => {
    planConfigService.getConfigCd(IP_CONFIG_PARAMS)
      .then((response) => {
        const { data } = response;
        if (!isEmpty(data)) {
          const { objVal } = data;

          setPolicy(objVal);
        }
      })
      .catch((error) => {
        debug(error);
      })
  }
  
  const createVersionName = () => {
    planVersionService.createVersionName({ moduleCd })
    .then(response => {
      if (response.data) {
        const generateVersionName = response.data;
        setNewScenarioRow(prev => ({ ...prev, name: generateVersionName}));
      }
    })
    .catch(e => {
      debug(e);
      const verNm = `${moduleCd}${dateUtils.getToday()}S001`;
      setNewScenarioRow(prev => ({ ...prev, name: verNm}));
    });
  }

  const refreshConfig = () => {
    selectedData.current = defaultScenario;
    setNewScenarioRow(selectedData.current);
    setModalOpen(false);
    setClassification(null);
  }

  const handleClose = (_e, action) => {
    if (action === "cancel") {
      refreshConfig();
    }
  };

  const handleSave = () => {
    if (classification !== null) {
      const policyObj = newScenarioRow?.policyObj;

      if (!isNil(policyObj)) {
        policyObj[SIM_TARGET_FILTER_POLICY] = simTargetFilterPolicy;
      }

      /* 고급 설정 필터 사용이후 셋팅된 데이터가 존재하지 않는다면 저장되지 않도록 데이터 검증 */
      if (simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED]) {
        if (isEmpty(simTargetFilterPolicy?.[TARGET_LOCATION])) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoSelectedSimTargetLocation") });
          return;
        }

        if (isEmpty(simTargetFilterPolicy?.[TARGET_ITEM_HRCHY])) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoSelectedSimTargetItemHrchy") });
          return;
        }
      }

      targetInvSimulationService.upsert([
        { 
          ...newScenarioRow, 
          classificationDto: classification, 
          sourceVersionId: sourceVersion.id,
          
        }])
        .then((response) => {
          if (response.status === HttpStatusCode.NoContent) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DuplicateVersion") });
            return;
          }
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          refreshConfig();
          retrievePlanVersion();
          reload(true);
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NotAppliedClassifi") });
    }
  }

  const onChangeClassification = obj => {
    if (obj !== null) {
      const findClassification = classificationList.find((item) => item.name === obj.name);
      setClassification(findClassification);
    } else {
      setClassification(null);
    }
  }

  const handleTfChange = useCallback(e => {
    const {name, value} = e.target;

    setNewScenarioRow(originData => ({
      ...originData,
      [name]: value
    }));

  }, []);

  useEffect(() => {
    createVersionName();
    setSourceVersion(masterData);
    setSourceData([masterData, ...planVersions]);

    if (modalOpen) {
      retrieveIpConfig();
    }

   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // 고급 대상 필터를 켜면 우측 패널이 붙어 폭이 늘어난다 — 프리셋으로는 표현이 안 돼 width 를 직접 준다
  const modalWidth = (simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED] ? MODAL_WIDTH_EXPANDED : MODAL_WIDTH) + 32;

  return (
    /* handleSave 가 targetInvSimulationService.upsert() 를 호출한다 — 실제로 저장하므로 save + SaveIcon(셸 기본값). */
    <BaseConfigModal
      open={modalOpen}
      onClose={(e) => {
        handleClose(e, "cancel")
      }}
      title={t("createScenario")}
      onSave={handleSave}
      width={modalWidth}
    >
        <Box width={"100%"} sx={simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED] ? { display: "flex" } : { display: "block" }}>
          <Box minWidth={MODAL_WIDTH} width={MODAL_WIDTH}>
            <FormControl size="small" sx={{ mt: 2, width: "100%" }} key={"source"}>
              <VersionAutocomplete
                publishToChat={false}
                options={sourceData}
                value={sourceVersion}
                onChange={(_, newValue) => onChangeSourceData(newValue)}
                label={t("sourceData")}
                getIcon={(option) => (isNull(option.id) ? null : (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon))}
                getIconColor={iconColor}
                getTooltip={(option) => {
                  const c = option?.planConfigObj;
                  return c?.PLAN_START_DATE && c?.PLAN_END_DATE ? `${c.PLAN_START_DATE} ~ ${c.PLAN_END_DATE}` : null;
                }}
                showInputIcon={false}
                sx={{ mr: 0, minWidth: 0, width: "100%" }}
              />
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <TextField
                id="scenarioNameTF"
                name="name"
                sx={{ width: "100%" }}
                label={t("scenarioName")}
                variant="outlined"
                onChange={handleTfChange}
                value={newScenarioRow?.name}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                id={"abc-xyz-classification-list"}
                options={classificationList}
                getOptionLabel={(option) => option.name ?? ""}
                value={classification}
                defaultValue={null}
                onChange={(_, newValue) => onChangeClassification(newValue)}
                isOptionEqualToValue={(option, value) => {
                  return option.name === value.name;
                }}
                size="small"
                sx={{ mr: 1.5, minWidth: "100%" }}
                renderInput={(params) => (
                  <TextField {...params} label={t("abcXyzClassification")} />
                )}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                id="descriptionTF"
                name="descTxt"
                sx={{ width: "100%" }}
                label={t("descTxt")}
                variant="outlined"
                multiline
                rows={2}
                onChange={handleTfChange}
                value={newScenarioRow.descTxt}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <IpTargetLocationItemHrchyEnabledComponent
              t={t}
              simTargetFilterPolicy={simTargetFilterPolicy}
              setSimTargetFilterPolicy={setSimTargetFilterPolicy}
              label={t("advancedSettings")}
              labelPlacement={"start"}
              switchSx={{ mt: 1, mr: 0.5 }}
              sx={{ ml: 0.5, width: "100%", justifyContent: "space-between"}}
            />
          </Box>
          {simTargetFilterPolicy?.[SIM_TARGET_FILTER_ENABLED] &&
            <Box sx={{ ml: `${ADVANCED_PANEL_GAP}px` }}>
              <IpTargetLocationItemHrchyComponentContent
                t={t}
                viewName={viewName}
                simTargetFilterPolicy={simTargetFilterPolicy}
                setSimTargetFilterPolicy={setSimTargetFilterPolicy}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                contentStyles={{ mt: 2 }}
                filterStyles={{ minHeight: 90, maxHeight: 90 }}
              />
            </Box>}
        </Box>
    </BaseConfigModal>
  );
};

export default CreateScenarioModal;